import { useState, useContext, useEffect, useMemo } from "react"
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from "react-router-dom"
import _ from "lodash"
import { useQuery } from "@apollo/client"

// Context
import { UserContext } from "../../context/userContext"
import { FormContext } from "../../context/formContext"

// Helpers
import ParticipantProfileBoxSmall from "../../components/ParticipantProfileBoxSmall"
import { cleanupTmpFields } from "../../components/FieldTypes/helpers/cleanupTempFields"

// Queries
import {
  GET_ENTERPRISE_FIELD_DEFS,
  GET_TEMPLATE,
  GET_TEMPLATE_AUDIT_TYPES,
} from "./queries"

// Comps
import FormNav from "./FormNav"
import FormContainer from "./FormContainer"
import FormSubmitting from "./FormSubmitting"
import FormErrorPanel from "./components/FormErrorPanel"
import TemplateAvailability from "./components/TemplateAvailability"
import { LoaderMedium } from "../../components/Loaders"
import templatesWithParticipantProfile from "../../resources/showParticipantProfileBoxSmall.json"
import redirect_url from "../../resources/redirectTemplate.json"
import gaTracking, { VERSION } from "../../helpers/gaTracking"
import { buildFieldDefMap } from "../../helpers/dateNormalization"
import { LogDebug, LogInfo, LogWarning } from "../../helpers/logger"
import { logApolloError } from "../../helpers/apolloErrorLogger"
import PreviousSession from "./components/PreviousSession"
import GPRADischarge from "./components/GPRADischargeDialog"

// Hooks
import useFormSubmit from "./hooks/useFormSubmit"
import useGPRAStatus from "./hooks/useGPRAStatus"
import { getPrefix } from "./helpers/FormNavHelpers"

import styles from "../../styles/Form.module.css"

// List of blocks to exclude from intake session edits
const intakeEditExcludedBlocks = [
  "participant__consent",
  "participant",
  "participant__membership",
]

// Participant templates that do not require participant_id.
const participantIdGateExclusions = ["group_session"]

export default function FormBuilder() {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const location = useLocation()
  const previousUrl = location.pathname
  let { idTemplate, formPage, idStamp = null } = useParams()
  let hiddenTitle = false

  // Utility to filter out specific blocks under certain conditions
  function filterIntakeSessionEditBlocks(blocks, idTemplate, editMode) {
    if (
      (idTemplate === "intake_session" ||
        idTemplate === "intake_reactivation_session") &&
      editMode &&
      Array.isArray(blocks)
    ) {
      const filteredBlocks = blocks.filter(
        (block) => !intakeEditExcludedBlocks.includes(block.block_name),
      )
      return filteredBlocks
    }

    return blocks || []
  }

  if (
    idTemplate === "account_preferences" &&
    idStamp !== userCtx?.session?.user?.staff_id_stamp
  ) {
    LogInfo(
      "console.....",
      idStamp,
      "..............",
      userCtx?.session?.user?.staff_id_stamp,
    )
    navigate(
      `/form/account_preferences/1/${userCtx?.session?.user?.staff_id_stamp}`,
    )
  }

  if (idTemplate === "enterprise_profile") {
    idTemplate = "enterprise"
    hiddenTitle = true
  }

  const [referringLocation, setReferringLocation] = useState(undefined) //If an origin is provided in state, use it to navigate on form completion
  const [referringReturnOrigin, setReferringReturnOrigin] = useState(undefined) //If the referring location has a return origin, store it
  const [availability, setAvailability] = useState(true)
  const [blocks, setBlocks] = useState([])
  const [globalValues, setGlobalValues] = useState({})
  const [templateInfo, setTemplateInfo] = useState([])
  const [templateTitle, setTemplateTitle] = useState("")
  const [resubmitCount, setResubmitCount] = useState(0) // Used to limit number of resubmissions allowed in FormSubmitting component
  const [formError, setFormError] = useState(null)

  const [
    fetchingGPRA,
    showPopup,
    ableToDelete,
    gpraRecordId,
    dischargeCompleted,
  ] = useGPRAStatus()

  const [blocksRequired, setBlocksRequired] = useState([])
  const [blocksSaved, setBlocksSaved] = useState({})
  const [blocksEdited, setBlocksEdited] = useState({})
  const [blocksFetched, setBlocksFetched] = useState({})
  const [templateCategory, setTemplateCategory] = useState("")
  const [templateType, setTemplateType] = useState("")

  // Track field loading states
  // Note: Formik already tracks touched/dirty states, but because many of our field
  // components call setFieldValue, to set default values on load, Formik always reports dirty=true
  const [loadingFields, setLoadingFields] = useState({})

  const isFieldRegistrationAllowed = (field) => {
    // Don't allow for conditional fields, or multi-fields
    if (field?.field_conditional || field?.__typename === "TypeMultiField") {
      return false
    }

    return true
  }

  function registerLoadingField(field) {
    // Check if registration is allowed
    if (!isFieldRegistrationAllowed(field)) {
      return
    }

    // If fieldName is empty, log a warning and return
    const fieldName = field?.field_name
    if (!fieldName) {
      LogWarning("registerLoadingField called with empty field name:", field)
      return
    }

    // Add this field to loadingFields
    setLoadingFields((prev) => ({
      ...prev,
      [fieldName]: true,
    }))
  }

  function completeLoadingField(field) {
    // Check if registration is allowed
    if (!isFieldRegistrationAllowed(field)) {
      return
    }

    // If fieldName is empty, log a warning and return
    const fieldName = field?.field_name
    if (!fieldName) {
      LogWarning("completeLoadingField called with empty field name:", field)
      return
    }

    // Set this field to loaded
    setLoadingFields((prev) => ({
      ...prev,
      [fieldName]: false,
    }))
  }

  function clearLoadingFields() {
    setLoadingFields({})
  }

  // bundle Formik state into a single object (keeps legacy setters/keys for compatibility)
  const [formikState, setFormikState] = useState({
    isValid: false,
    values: {},
    submitForm: null,
  })

  const { v4: uuidv4 } = require("uuid")
  const [formId] = useState(uuidv4())

  useEffect(() => {
    if (location.state?.origin) {
      setReferringLocation(location.state.origin)
      setReferringReturnOrigin(location.state?.returnOrigin)
    }
  }, [location.state])

  const {
    submitting,
    message,
    err,
    committedBeforeError,
    activeStep,
    transactionId,
    initSubmit,
  } = useFormSubmit()

  const PARTICIPANT_ID = userCtx.current_participant_id || ""
  const SUPERVISION_STAFF_MEMBERSHIP_ID =
    userCtx.current_staff_membership_id || ""

  const TYPE = "system"
  const EDIT_MODE = idStamp ? true : false

  // Preload template audit types so participant requirements can be decided before GET_TEMPLATE.
  const {
    data: templateAuditTypesData,
    loading: templateAuditTypesLoading,
    error: templateAuditTypesError,
  } = useQuery(GET_TEMPLATE_AUDIT_TYPES, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "cache-and-network",
    onCompleted: () => {
      // Do not clear formError here to avoid clearing an active main form load error.
    },
    onError: (error) => {
      logApolloError("Error loading form template metadata:", error)
      setFormError(error)
    },
  })

  // Build an idTemplate -> audit_type lookup once per metadata refresh.
  const templateAuditTypeMap = useMemo(
    () =>
      new Map(
        (templateAuditTypesData?.templates || []).map((template) => [
          template.idTemplate,
          template.audit_type || "",
        ]),
      ),
    [templateAuditTypesData?.templates],
  )

  const templateAuditTypeFromMetadata = templateAuditTypeMap.get(idTemplate) || ""
  const isParticipantTemplate = templateAuditTypeFromMetadata === "participant"
  const hasParticipantId = !_.isEmpty(PARTICIPANT_ID)

  // Participant-scoped templates require participant_id, except intake_session create mode.
  const requiresParticipant =
    isParticipantTemplate &&
    !participantIdGateExclusions.includes(idTemplate) &&
    (idTemplate !== "intake_session" || EDIT_MODE)

  // Wait while audit type is unresolved and no participant is selected yet.
  const shouldWaitForTemplateAuditType =
    !_.isEmpty(idTemplate) &&
    !hasParticipantId &&
    templateAuditTypesLoading &&
    !templateAuditTypesError &&
    _.isEmpty(templateAuditTypeFromMetadata)

  // Clear participant context when this form should run in a non-participant scope.
  useEffect(() => {
    const clearParticipantFromCtx = async () => await userCtx.clearParticipant()
    const clearParticipantTemplateList = ["intake_session"]
    const shouldClearForTemplate =
      !EDIT_MODE && clearParticipantTemplateList.includes(idTemplate)
    const shouldClearForAuditType =
      !_.isEmpty(templateAuditTypeFromMetadata) && !isParticipantTemplate

    if (shouldClearForTemplate || shouldClearForAuditType) {
      LogDebug("Clearing participant from ctx for:", {
        idTemplate,
        templateAuditType: templateAuditTypeFromMetadata,
        editMode: EDIT_MODE,
      })
      clearParticipantFromCtx()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    idTemplate,
    templateAuditTypeFromMetadata,
    isParticipantTemplate,
    EDIT_MODE,
  ])

  const handleSubmit = async () => {
    const postBody = {
      idTemplate,
      organization_alias: userCtx.session.user.organization_alias,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      enterprise_alias: userCtx.session.user.enterprise_alias,
      participant_alias: userCtx.participant_alias || "not-set",
      participant_membership_id: userCtx.participant_membership_id || "not-set",
      participant_id: PARTICIPANT_ID || "not-set",
      staff_alias: userCtx.session.user.staff_alias,
      staff_id: userCtx.session.user.staff_id,
      form_id: formId,
      template_info: JSON.stringify(templateInfo),
      version: VERSION,
      values: globalValues,
    }

    // Trigger Validation and Submission
    if (EDIT_MODE) {
      postBody.idStamp = idStamp
    }

    if (idTemplate === "intake_reactivation_session") {
      await userCtx.changeParticipant(userCtx.current_participant_id)
    }

    await initSubmit(postBody, dateFieldDefMap)
  }

  const handleFetchedBlock = (block) => {
    // Add the fetched block definition to the object
    setBlocksFetched((prev) => ({
      ...prev,
      [block.block_name]: block,
    }))
  }

  const getMatchedTopLevelBlockValues = (blockId, values) => {
    const fetchedBlock = blocksFetched[blockId]

    if (!fetchedBlock) {
      LogWarning(
        `Attempting to save block ${blockId} which has not been fetched yet. Skipping block save.`,
      )
      return null
    }

    const cleanedValues = cleanupTmpFields(values) || {}
    const expectedFieldNames = _.map(fetchedBlock.fields || [], "field_name")
    const valueKeys = Object.keys(cleanedValues)
    const matchedKeys = _.intersection(valueKeys, expectedFieldNames)
    const missingKeys = _.difference(expectedFieldNames, valueKeys)

    if (missingKeys.length > 0) {
      LogWarning(
        `Attempting to save block ${blockId} without all fetched block fields present. Skipping block save.`,
        {
          blockId,
          matchedKeys,
          missingKeys,
          expectedFieldNames,
        },
      )
      return null
    }

    return _.pick(cleanedValues, expectedFieldNames)
  }

  const handleSaveBlock = (blockId, values) => {
    const matchedValues = getMatchedTopLevelBlockValues(blockId, values)
    if (!matchedValues) {
      return
    }

    /*Google Analytics tracking*/
    const gaTrackingParams = {
      event: "template_step",
      userCtx,
      payload: {
        template_name: idTemplate,
        step_modality: "saved_continue",
        block_name: blockId,
        stamp_id: idStamp || "",
        template_category: templateCategory,
        form_id: formId,
      },
    }
    gaTracking(gaTrackingParams)
    /*End Google Analytics tracking*/

    setGlobalValues({
      ...cleanupTmpFields(globalValues),
      [blockId]: cleanupTmpFields(values),
    })

    setBlocksEdited({
      ...blocksEdited,
      [blockId]: false,
    })
    return setBlocksSaved({
      ...blocksSaved,
      [blockId]: true,
    })
  }

  const handleEditBlock = (blockId) => {
    setBlocksSaved({
      ...blocksSaved,
      [blockId]: false,
    })
    return setBlocksEdited({
      ...blocksEdited,
      [blockId]: true,
    })
  }

  const saveTemplateInfo = (info) => {
    setTemplateInfo((prevInfo) => ({
      ...prevInfo,
      ...info,
    }))
  }

  let variables = {
    staff_id: userCtx.session.user.staff_id,
    organization_id: userCtx.session.user.organization_id,
    enterprise_id: userCtx.session.user.enterprise_id,
    idTemplate,
    type: TYPE,
    participant_id: PARTICIPANT_ID,
    supervision_staff_membership_id: SUPERVISION_STAFF_MEMBERSHIP_ID,
    editMode: EDIT_MODE,
  }

  if (EDIT_MODE) {
    variables.idStamp = idStamp
    variables.retrieveLatestAssessment = true
  }

  const { loading, data } = useQuery(GET_TEMPLATE, {
    variables,
    skip:
      !userCtx.session.user.staff_id ||
      shouldWaitForTemplateAuditType ||
      (requiresParticipant && !hasParticipantId),
    fetchPolicy: "no-cache",
    onCompleted: async (data) => {
      setFormError(null)

      const notAvailableTemplate = data.template.template_availability === 0
      if (notAvailableTemplate) {
        setAvailability(false)
        return
      }

      // Filter blocks if needed using utility
      const filteredBlocks = filterIntakeSessionEditBlocks(
        data.template.blocks,
        idTemplate,
        EDIT_MODE,
      )

      const sortedBlocks = _.sortBy(filteredBlocks, ["index"])
      const initBlocksSaved = _.reduce(
        filteredBlocks,
        (acc, block) => {
          return {
            ...acc,
            [block.block_name]: false,
          }
        },
        {},
      )
      const alwaysRequiredBlocks = [
        {
          templateId: "ad_hoc_referral",
          blocks: ["referral"],
        },
        {
          templateId: "ad_hoc_assessment",
          blocks: ["session__assessment_recovery_capital"],
        },
        {
          templateId: "ad_hoc_recovery_capital_assessment",
          blocks: ["session__assessment_recovery_capital"],
        },
      ]
      const initBlocksRequired = _.reduce(
        filteredBlocks,
        (acc, block) => {
          const requiredBlock = _.find(alwaysRequiredBlocks, {
            templateId: idTemplate,
          })
          if (requiredBlock) {
            if (requiredBlock.blocks.includes(block.block_name)) {
              return [...acc, block.block_name]
            }
          }
          if (!block.system_required) return acc
          return [...acc, block.block_name]
        },
        [],
      )

      // Store template info for later use
      setTemplateCategory(data.template.template_category)
      setTemplateType(data.template.template_type)
      setTemplateTitle(data.template.template_title)

      /*Google Analytics tracking*/
      const gaTrackingParams = {
        event: "template_begin",
        userCtx,
        payload: {
          template_name: data.template.template_name,
          template_category: data.template.template_category,
          stamp_id: idStamp || "",
          form_id: formId,
        },
      }
      gaTracking(gaTrackingParams)
      /*End Google Analytics tracking*/

      setBlocksSaved(initBlocksSaved)
      setBlocksRequired(initBlocksRequired)

      // Find the last block and set isLastBlock=false
      const lastBlockIndex = sortedBlocks.length - 1
      sortedBlocks[lastBlockIndex].isLastBlock = false

      // Inject artificial final/last block for submission
      if (!sortedBlocks.some((b) => b.block_name === "submit_block")) {
        sortedBlocks.push({
          block_name: "submit_block",
          block_title: "Ready to Submit",
          idBlock: "submit_block",
          index: data.template.total_blocks + 1,
          system_required: false,
          total_fields: 0,
          isLastBlock: true,
        })
      }

      const currentPageNum = Number(formPage)
      const hasCurrentPage = sortedBlocks.some(
        (block) => Number(block.index) === currentPageNum,
      )

      if (sortedBlocks.length > 0 && !hasCurrentPage) {
        const fallbackPage = Number(sortedBlocks[0].index)
        const prefix = getPrefix(idTemplate, previousUrl)
        const pattern = EDIT_MODE
          ? `${prefix}/:formPage/${idStamp}`
          : `${prefix}/:formPage/`
        const fallbackPath = generatePath(pattern, { formPage: fallbackPage })

        LogWarning("Invalid form page after block filtering. Redirecting.", {
          idTemplate,
          editMode: EDIT_MODE,
          requestedPage: currentPageNum,
          fallbackPage,
          blockIndexes: sortedBlocks.map((b) => b.index),
        })

        navigate(fallbackPath, { replace: true })
      }

      setBlocks(sortedBlocks)
    },
    onError: (error) => {
      logApolloError("Error loading form template:", error)
      setFormError(error)
    },
  })

  const { data: enterpriseFieldDefsData } = useQuery(
    GET_ENTERPRISE_FIELD_DEFS,
    {
      variables: {
        enterprise_id: userCtx.session.user.enterprise_id,
      },
      skip: !userCtx.session.user.enterprise_id,
      fetchPolicy: "cache-and-network",
    },
  )

  const dateFieldDefMap = useMemo(() => {
    return buildFieldDefMap(
      enterpriseFieldDefsData?.fieldDefs || [],
      "FormBuilder",
    )
  }, [enterpriseFieldDefsData?.fieldDefs])

  const dataPropagation = useMemo(
    () => (data?.template?.values ? JSON.parse(data.template.values) : {}),
    [data?.template?.values],
  )

  const template = data?.template

  // This form needs a participant first, show guidance instead of a loading spinner.
  if (requiresParticipant && !hasParticipantId) {
    return (
      <div className={styles.formLoadErrorContainer}>
        <FormErrorPanel
          title="Participant required"
          subHeader={
            <h3>
              <span className={styles.errorContainerSubHeader}>
                Select a participant to load this form.
              </span>
            </h3>
          }
          messageText="This form is participant-scoped and cannot be loaded until a participant is selected."
          enableCopyButton={false}
        />
      </div>
    )
  }

  if (loading || fetchingGPRA || (!template && !formError)) {
    return <LoaderMedium />
  }

  if (formError) {
    const fallbackErrorMessage =
      "Something went wrong while loading this form. Please try again. If this persists, contact support."
    const actualErrorMessage = _.isString(formError)
      ? formError
      : formError?.message
    const combinedErrorMessage = _.isEmpty(actualErrorMessage)
      ? fallbackErrorMessage
      : `${fallbackErrorMessage} ${actualErrorMessage}`

    return (
      <div className={styles.formLoadErrorContainer}>
        <FormErrorPanel
          title="Unable to load form"
          subHeader={
            <h3>
              <span className={styles.errorContainerSubHeader}>
                We could not load this form.
              </span>
            </h3>
          }
          messageText={combinedErrorMessage}
          enableCopyButton={false}
        />
      </div>
    )
  }

  const handleGoBack = () => {
    const path = localStorage.getItem("groupSessionListPage")

    if (referringLocation) {
      if (EDIT_MODE && referringReturnOrigin) {
        navigate(referringLocation, {
          state: { origin: referringReturnOrigin },
        })
      } else {
        navigate(referringLocation)
      }
    } else if (
      previousUrl.includes("/enterprise/form/program/1/") &&
      idTemplate === "program"
    ) {
      navigate("/enterprise/programs")
    } else if (
      previousUrl.includes("/enterprise/form/location/1/") &&
      idTemplate === "location"
    ) {
      navigate("/enterprise/locations")
    } else if (
      previousUrl.includes("/library/enterprise_profile/") &&
      idTemplate === "enterprise"
    ) {
      navigate("/enterprise/library")
    } else if (
      previousUrl.includes("/enterprise/form/staff/") &&
      idTemplate === "staff"
    ) {
      navigate("/enterprise/staff")
    } else if (
      path?.includes("/organization/staff/supervision-session/group") &&
      idTemplate === "group_supervision_session"
    ) {
      localStorage.removeItem("groupSessionListPage")
      navigate("/organization/staff/supervision-session/group")
    } else if (
      path?.includes("/supervision/group-session") &&
      idTemplate === "group_supervision_session"
    ) {
      localStorage.removeItem("groupSessionListPage")
      navigate("/supervision/group-session")
    } else {
      const redirectUrls =
        _.find(redirect_url, { idTemplate }) || "/my-participants"
      navigate(redirectUrls.redirect)
    }
  }

  if (submitting) {
    return (
      <FormSubmitting
        activeStep={activeStep}
        error={err}
        committedBeforeError={committedBeforeError}
        transactionId={transactionId}
        idStamp={idStamp}
        formId={formId}
        templateData={data}
        message={message}
        onGoBack={handleGoBack}
        resubmitCount={resubmitCount}
        resubmit={() => {
          setResubmitCount((prev) => prev + 1)
          handleSubmit()
        }}
      />
    )
  }

  const formContextValue = {
    handleSaveBlock,
    handleEditBlock,
    handleFetchedBlock,
    handleSubmit,
    saveTemplateInfo,
    registerLoadingField,
    completeLoadingField,
    clearLoadingFields,
    formError,
    setFormError,
    setFormikState,
    blocksRequired,
    blocksSaved,
    blocksEdited,
    blocksFetched,
    blocksFiltered: blocks,
    idTemplate,
    requiresParticipant,
    loadingFields,
    type: TYPE,
    participant_id: PARTICIPANT_ID,
    supervision_staff_membership_id: SUPERVISION_STAFF_MEMBERSHIP_ID,
    idStamp,
    editMode: EDIT_MODE,
    currentPage: Number(formPage),
    isLastBlock: template.total_blocks === Number(formPage),
    globalValues,
    dateFieldDefMap,
    formikState, // Expose limited Formik state
  }

  // Redirect after successful submission
  const formSubmitted = activeStep === 3 && !submitting
  if (formSubmitted) {
    // Clear participant from ctx for certain templates
    const idTemplateListToClear = ["discharge_session"]
    const shouldClearParticipantCtx = idTemplateListToClear.includes(idTemplate)

    if (shouldClearParticipantCtx) {
      LogInfo("Clearing participant from ctx for idTemplate:", idTemplate)
      userCtx.clearParticipant()
    }

    userCtx.changeParticipant(PARTICIPANT_ID)
    // Complete and redirect
    handleGoBack()

    return null
  }

  if (!availability) return <TemplateAvailability />

  return (
    <FormContext.Provider value={formContextValue}>
      <GPRADischarge
        visible={showPopup}
        ableToDelete={ableToDelete}
        gpraRecordId={gpraRecordId}
        dischargeCompleted={dischargeCompleted}
      />

      {!_.isEmpty(_.find(templatesWithParticipantProfile, { idTemplate })) && (
        <div className="form-participant">
          <ParticipantProfileBoxSmall />
          <br />
        </div>
      )}

      {!hiddenTitle && (
        <div className={styles.templateTitleContainer}>
          <div>
            <h1 className={styles.templateTitle}>
              {template.template_title}
            </h1>
            <div className={styles.templateDescription}>
              {template.template_description}
            </div>
          </div>
          <PreviousSession
            templateId={idTemplate}
            templateType={templateType}
            templateTitle={templateTitle}
            blocks={blocks}
          />
        </div>
      )}
      <div className={styles.formContainer}>
        <div className={styles.formNavSection}>
          <FormNav blocks={blocks} templateCategory={templateCategory} />
        </div>
        <FormContainer
          blocks={blocks}
          globalValues={globalValues}
          dataPropagation={dataPropagation}
        />
      </div>
    </FormContext.Provider>
  )
}
